import { useState } from 'react';
import type { ComponentType } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import type { Trace } from '@/features/traceability/type/Tool';
import type { ChartConfig } from '../../type/dynamicChart';
import { TLScatterChart } from './TLScatterChart';
import { TLBarChart } from './TLBarChart';
import { TLHistogramChart } from './TLHistogramChart';
import { TLPieChart } from './TLPieChart';
import { TLRadarChart } from './TLRadarChart';
import { TLBoxPlotChart } from './TLBoxPlotChart';
import { chartTypeRegistry } from '../../registry/chartTypes';

type ChartComponentProps = {
  config: ChartConfig;
  traces: Trace[];
};

// The registry stores metadata only (no React components) to avoid circular imports.
// The component lookup lives here, co-located with the imports.
const chartComponentMap: Record<string, ComponentType<ChartComponentProps>> = {
  scatter: TLScatterChart,
  bar: TLBarChart,
  histogram: TLHistogramChart,
  pie: TLPieChart,
  radar: TLRadarChart,
  boxplot: TLBoxPlotChart,
};

type DynamicChartProps = {
  config: ChartConfig;
  traces: Trace[];
  onOpenConfig?: () => void;
};

function StaleMetricBanner({
  reason,
  onOpenConfig,
  onDismiss,
}: {
  reason: string;
  onOpenConfig?: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Callout
      tone="warning"
      className="mb-3"
      title={t('analytics.dashboard.complexCharts.stale.title')}
      action={
        <>
          {onOpenConfig && (
            <Button variant="outline" size="sm" onClick={onOpenConfig}>
              {t('analytics.dashboard.complexCharts.stale.editConfig')}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            {t('analytics.dashboard.complexCharts.stale.dismiss')}
          </Button>
        </>
      }
    >
      <span className="block font-mono text-xs uppercase tracking-widest text-muted-foreground">
        {t('analytics.dashboard.complexCharts.stale.code')}
      </span>
      <code className="mt-1 inline-block break-all rounded bg-muted/60 px-2 py-0.5 text-xs text-foreground">
        {reason}
      </code>
    </Callout>
  );
}

function ChartRenderer({ config, traces }: { config: ChartConfig; traces: Trace[] }) {
  const { t } = useTranslation();

  if (!config || !config.type || !config.xAxis || !config.yAxis) {
    return (
      <div className="flex h-[400px] items-center justify-center text-muted-foreground">
        <p>{t('analytics.charts.errors.invalidConfig')}</p>
      </div>
    );
  }

  if (!Array.isArray(traces)) {
    return (
      <div className="flex h-[400px] items-center justify-center text-muted-foreground">
        <p>{t('analytics.charts.errors.noData')}</p>
      </div>
    );
  }

  const typeDef = chartTypeRegistry.find((ct) => ct.key === config.type);

  if (!typeDef) {
    return (
      <div className="flex h-[400px] items-center justify-center text-muted-foreground">
        <p>{t('analytics.charts.errors.unknownType')}</p>
      </div>
    );
  }

  const validity = typeDef.isConfigValid(config);
  if (!validity.valid) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center gap-2 text-muted-foreground">
        <p className="font-medium">{t('analytics.charts.errors.invalidConfig')}</p>
        {validity.reason && <p className="text-sm">{validity.reason}</p>}
      </div>
    );
  }

  const Component = chartComponentMap[config.type];

  if (!Component) {
    return (
      <div className="flex h-[400px] items-center justify-center text-muted-foreground">
        <p>{t('analytics.charts.errors.unknownType')}</p>
      </div>
    );
  }

  return <Component config={config} traces={traces} />;
}

export function DynamicChart({ config, traces, onOpenConfig }: DynamicChartProps) {
  const [dismissed, setDismissed] = useState(false);

  const showBanner = Boolean(config.invalidReason) && !dismissed;

  return (
    <div>
      {showBanner && config.invalidReason && (
        <StaleMetricBanner
          reason={config.invalidReason}
          onOpenConfig={onOpenConfig}
          onDismiss={() => setDismissed(true)}
        />
      )}
      <div
        className={
          showBanner ? 'pointer-events-none h-[120px] overflow-hidden opacity-15 grayscale' : undefined
        }
      >
        <ChartRenderer config={config} traces={traces} />
      </div>
    </div>
  );
}
